import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  FileText, 
  Upload, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Eye, 
  Download,
  RefreshCw,
  Sparkles,
  Edit3,
  Check,
  X,
  FileCheck,
  Bot
} from 'lucide-react';
import api from '../lib/api';

// Status configurations
const STATUS_CONFIG = {
  caricato: { label: 'Caricato', color: 'bg-gray-500', icon: FileText },
  in_elaborazione: { label: 'In Elaborazione', color: 'bg-blue-500', icon: Loader2 },
  estratto: { label: 'Estratto', color: 'bg-amber-500', icon: Sparkles },
  da_validare: { label: 'Da Validare', color: 'bg-orange-500', icon: AlertCircle },
  validato: { label: 'Validato', color: 'bg-green-500', icon: CheckCircle2 },
  errore: { label: 'Errore', color: 'bg-red-500', icon: AlertCircle }
};

// Field labels mapping
const FIELD_LABELS = {
  // Cliente
  cliente_nome: 'Nome',
  cliente_cognome: 'Cognome',
  cliente_codice_fiscale: 'Codice Fiscale',
  cliente_partita_iva: 'Partita IVA',
  cliente_indirizzo_residenza: 'Indirizzo Residenza',
  cliente_citta_residenza: 'Città',
  cliente_cap_residenza: 'CAP',
  cliente_provincia_residenza: 'Provincia',
  cliente_telefono: 'Telefono',
  cliente_email: 'Email',
  cliente_nato_a: 'Nato a',
  cliente_data_nascita: 'Data Nascita',
  cliente_attivita_lavorativa: 'Attività Lavorativa',
  
  // Sito
  sito_indirizzo: 'Indirizzo Sito',
  sito_citta: 'Città Sito',
  sito_cap: 'CAP Sito',
  sito_provincia: 'Provincia Sito',
  sito_latitudine: 'Latitudine',
  sito_longitudine: 'Longitudine',
  sito_foglio_catastale: 'Foglio Catastale',
  sito_particella: 'Particella',
  sito_uso_immobile: 'Uso Immobile',
  
  // Impianto
  impianto_potenza_nominale_kw: 'Potenza Nominale (kW)',
  impianto_codice_soluzione: 'Codice Soluzione',
  impianto_codice_prodotto: 'Codice Prodotto',
  impianto_tipo_installazione: 'Tipo Installazione',
  impianto_tipo_struttura: 'Tipo Struttura',
  impianto_storage_batterie: 'Storage Batterie',
  
  // Contatore
  contatore_pod: 'POD',
  contatore_livello_tensione: 'Livello Tensione',
  contatore_potenza_disponibile_kw: 'Potenza Disponibile (kW)',
  contatore_potenza_impegnata_kw: 'Potenza Impegnata (kW)',
  
  // Finanziamento
  finanziamento_tipo: 'Tipo Finanziamento',
  finanziamento_durata_mesi: 'Durata (mesi)',
  detrazione_fiscale: 'Detrazione Fiscale',
  
  // Banca
  banca_intestatario_nome: 'Nome Intestatario',
  banca_intestatario_cognome: 'Cognome Intestatario',
  banca_intestatario_cf: 'CF Intestatario',
  banca_istituto: 'Istituto di Credito',
  banca_iban: 'IBAN',
  banca_swift: 'SWIFT',
  
  // Proprietà
  proprieta_percentuale: '% Proprietà',
  proprieta_tipo_diritto: 'Tipo Diritto',
  
  // Titolare PDC
  titolare_pdc_nome: 'Nome Titolare PDC',
  titolare_pdc_cognome: 'Cognome Titolare PDC',
  titolare_pdc_cf: 'CF Titolare PDC',
  titolare_pdc_piva: 'P.IVA Titolare PDC',
  
  // Consensi
  consenso_marketing: 'Consenso Marketing',
  consenso_ricerche_mercato: 'Consenso Ricerche',
  
  // Metadati
  data_compilazione: 'Data Compilazione',
  agenzia: 'Agenzia',
  nome_agente: 'Nome Agente',
  codice_agente: 'Codice Agente'
};

// Section groupings
const SECTIONS = [
  { 
    key: 'anagrafica', 
    title: 'Anagrafica Cliente', 
    icon: '👤',
    fields: ['cliente_nome', 'cliente_cognome', 'cliente_codice_fiscale', 'cliente_partita_iva', 
             'cliente_indirizzo_residenza', 'cliente_citta_residenza', 'cliente_cap_residenza', 
             'cliente_provincia_residenza', 'cliente_telefono', 'cliente_email', 'cliente_nato_a', 
             'cliente_data_nascita', 'cliente_attivita_lavorativa']
  },
  { 
    key: 'sito', 
    title: 'Dati Sito Impianto', 
    icon: '📍',
    fields: ['sito_indirizzo', 'sito_citta', 'sito_cap', 'sito_provincia', 
             'sito_latitudine', 'sito_longitudine', 'sito_foglio_catastale', 
             'sito_particella', 'sito_uso_immobile']
  },
  { 
    key: 'tecnici', 
    title: 'Dati Tecnici Impianto', 
    icon: '⚡',
    fields: ['impianto_potenza_nominale_kw', 'impianto_codice_soluzione', 'impianto_codice_prodotto',
             'impianto_tipo_installazione', 'impianto_tipo_struttura', 'impianto_storage_batterie']
  },
  { 
    key: 'contatore', 
    title: 'Dati Contatore', 
    icon: '🔌',
    fields: ['contatore_pod', 'contatore_livello_tensione', 'contatore_potenza_disponibile_kw', 
             'contatore_potenza_impegnata_kw']
  },
  { 
    key: 'finanziamento', 
    title: 'Finanziamento', 
    icon: '💳',
    fields: ['finanziamento_tipo', 'finanziamento_durata_mesi', 'detrazione_fiscale']
  },
  { 
    key: 'banca', 
    title: 'Dati Bancari', 
    icon: '🏦',
    fields: ['banca_intestatario_nome', 'banca_intestatario_cognome', 'banca_intestatario_cf',
             'banca_istituto', 'banca_iban', 'banca_swift']
  },
  { 
    key: 'proprieta', 
    title: 'Proprietà Immobile', 
    icon: '🏠',
    fields: ['proprieta_percentuale', 'proprieta_tipo_diritto']
  },
  { 
    key: 'titolare_pdc', 
    title: 'Titolare Punto Connessione', 
    icon: '👥',
    fields: ['titolare_pdc_nome', 'titolare_pdc_cognome', 'titolare_pdc_cf', 'titolare_pdc_piva']
  },
  { 
    key: 'consensi', 
    title: 'Consensi', 
    icon: '✅',
    fields: ['consenso_marketing', 'consenso_ricerche_mercato']
  },
  { 
    key: 'metadati', 
    title: 'Metadati Documento', 
    icon: '📄',
    fields: ['data_compilazione', 'agenzia', 'nome_agente', 'codice_agente']
  }
];

// ======================= VISURA CATASTALE CONFIGURATIONS =======================

// Field labels for Visura Catastale
const VISURA_FIELD_LABELS = {
  // Dati identificativi
  comune: 'Comune',
  codice_comune: 'Codice Comune',
  provincia: 'Provincia',
  foglio: 'Foglio',
  particella: 'Particella',
  subalterno: 'Subalterno',
  sezione: 'Sezione Urbana',
  
  // Classamento
  categoria: 'Categoria Catastale',
  classe: 'Classe',
  consistenza: 'Consistenza',
  rendita_catastale: 'Rendita Catastale (€)',
  superficie_totale_mq: 'Superficie Totale (mq)',
  superficie_escluse_scoperte_mq: 'Superficie (escl. scoperte) mq',
  
  // Indirizzo
  via: 'Via/Piazza',
  numero_civico: 'Numero Civico',
  piano: 'Piano',
  interno: 'Interno',
  
  // Intestatari
  intestatario_cognome: 'Cognome Proprietario',
  intestatario_nome: 'Nome Proprietario',
  intestatario_codice_fiscale: 'Codice Fiscale Proprietario',
  intestatario_quota: 'Quota Proprietà',
  intestatario_diritto: 'Tipo Diritto',
  intestatario_luogo_nascita: 'Luogo Nascita Proprietario',
  intestatario_data_nascita: 'Data Nascita Proprietario',
  intestatari_multipli: 'Più Intestatari',
  intestatari_count: 'Numero Intestatari',
  
  // Atto
  tipo_atto: 'Tipo Atto Provenienza',
  data_atto: 'Data Atto',
  notaio: 'Notaio',
  sede_notaio: 'Sede Notaio',
  repertorio: 'N. Repertorio',
  
  // Metadati
  tipo_visura: 'Tipo Visura',
  data_visura: 'Data Emissione',
  ufficio_provinciale: 'Ufficio Provinciale',
};

// Section groupings for Visura Catastale
const VISURA_SECTIONS = [
  { 
    key: 'identificativi', 
    title: 'Dati Identificativi Immobile', 
    icon: '🏢',
    fields: ['comune', 'codice_comune', 'provincia', 'sezione', 'foglio', 'particella', 'subalterno']
  },
  { 
    key: 'classamento', 
    title: 'Classamento e Rendita', 
    icon: '📊',
    fields: ['categoria', 'classe', 'consistenza', 'rendita_catastale', 'superficie_totale_mq', 'superficie_escluse_scoperte_mq']
  },
  { 
    key: 'indirizzo', 
    title: 'Indirizzo Catastale', 
    icon: '📍',
    fields: ['via', 'numero_civico', 'piano', 'interno']
  },
  { 
    key: 'intestatario', 
    title: 'Intestatario/Proprietario', 
    icon: '👤',
    fields: ['intestatario_cognome', 'intestatario_nome', 'intestatario_codice_fiscale', 'intestatario_quota', 'intestatario_diritto', 'intestatario_luogo_nascita', 'intestatario_data_nascita', 'intestatari_multipli', 'intestatari_count']
  },
  { 
    key: 'atto', 
    title: 'Atto di Provenienza', 
    icon: '📜',
    fields: ['tipo_atto', 'data_atto', 'notaio', 'sede_notaio', 'repertorio']
  },
  { 
    key: 'metadati_visura', 
    title: 'Metadati Documento', 
    icon: '📄',
    fields: ['tipo_visura', 'data_visura', 'ufficio_provinciale']
  }
];

// Helper function to get sections and labels based on document category
const getDocumentConfig = (documentCategory) => {
  if (documentCategory === 'visura_catastale') {
    return {
      sections: VISURA_SECTIONS,
      labels: VISURA_FIELD_LABELS,
      title: 'Visura Catastale',
      description: 'Verifica e modifica i dati estratti automaticamente dalla Visura Catastale'
    };
  }
  // Default: Modulo di Adesione
  return {
    sections: SECTIONS,
    labels: FIELD_LABELS,
    title: 'Modulo di Adesione',
    description: 'Verifica e modifica i dati estratti automaticamente dal Modulo di Adesione'
  };
};

/**
 * ExtractionStatusBadge - Shows extraction status with appropriate styling
 */
export const ExtractionStatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.caricato;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white flex items-center gap-1`}>
      <Icon className={`h-3 w-3 ${status === 'in_elaborazione' ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

/**
 * ExtractedFieldInput - Editable field with visual indicator for auto-extracted values
 */
const ExtractedFieldInput = ({ fieldKey, label: customLabel, value, originalValue, onChange, isModified }) => {
  // Use custom label if provided, otherwise fallback to FIELD_LABELS or formatted fieldKey
  const label = customLabel || FIELD_LABELS[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const hasValue = value !== null && value !== undefined && value !== '';
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        {hasValue && !isModified && (
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
            <Sparkles className="h-3 w-3 mr-1" />
            Auto-estratto
          </Badge>
        )}
        {isModified && (
          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
            <Edit3 className="h-3 w-3 mr-1" />
            Modificato
          </Badge>
        )}
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        className={`${hasValue && !isModified ? 'border-amber-300 bg-amber-50/50' : ''} ${isModified ? 'border-blue-300 bg-blue-50/50' : ''}`}
        placeholder={`Inserisci ${label.toLowerCase()}`}
      />
    </div>
  );
};

/**
 * ExtractionVerificationDialog - Dialog for verifying and editing extracted data
 */
export const ExtractionVerificationDialog = ({ 
  isOpen, 
  onClose, 
  extraction, 
  onValidate,
  documentPreviewUrl 
}) => {
  const [editedData, setEditedData] = useState({});
  const [modifiedFields, setModifiedFields] = useState(new Set());
  const [applyToCustomer, setApplyToCustomer] = useState(true);
  const [applyToContract, setApplyToContract] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  
  // Get document configuration based on category
  const documentCategory = extraction?.document_category || 'modulo_adesione';
  const docConfig = getDocumentConfig(documentCategory);
  const currentSections = docConfig.sections;
  const currentLabels = docConfig.labels;
  
  // Initialize edited data from extraction
  useEffect(() => {
    if (extraction?.flattened_data) {
      setEditedData(extraction.flattened_data);
      setModifiedFields(new Set());
      // Set first section as active tab
      const config = getDocumentConfig(extraction.document_category || 'modulo_adesione');
      if (config.sections.length > 0) {
        setActiveTab(config.sections[0].key);
      }
    }
  }, [extraction]);
  
  const handleFieldChange = (fieldKey, value) => {
    setEditedData(prev => ({ ...prev, [fieldKey]: value }));
    
    // Track if field was modified from original
    const originalValue = extraction?.flattened_data?.[fieldKey];
    if (value !== originalValue) {
      setModifiedFields(prev => new Set([...prev, fieldKey]));
    } else {
      setModifiedFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }
  };
  
  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await onValidate({
        extraction_id: extraction.id,
        validated_data: editedData,
        apply_to_customer: applyToCustomer,
        apply_to_contract: applyToContract
      });
      toast.success('Dati validati e applicati con successo');
      onClose();
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Errore durante la validazione');
    } finally {
      setIsValidating(false);
    }
  };
  
  // Custom field label getter that uses current document labels
  const getFieldLabel = (fieldKey) => {
    return currentLabels[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const totalFields = Object.keys(editedData).length;
  const filledFields = Object.values(editedData).filter(v => v !== null && v !== '').length;
  
  if (!extraction) return null;
  
  // Calculate tabs layout based on number of sections
  const tabsPerRow = Math.min(currentSections.length, 5);
  const firstRowSections = currentSections.slice(0, tabsPerRow);
  const secondRowSections = currentSections.slice(tabsPerRow);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            Verifica Dati Estratti - {docConfig.title}
          </DialogTitle>
          <DialogDescription>
            {docConfig.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 h-full">
            {/* Left side - Document Preview */}
            <div className="col-span-4 border rounded-lg overflow-hidden bg-gray-50">
              <div className="p-3 bg-gray-100 border-b flex items-center justify-between">
                <span className="text-sm font-medium">Documento Originale</span>
                {documentPreviewUrl && (
                  <Button variant="ghost" size="sm" asChild>
                    <a href={documentPreviewUrl} target="_blank" rel="noopener noreferrer">
                      <Eye className="h-4 w-4 mr-1" />
                      Apri
                    </a>
                  </Button>
                )}
              </div>
              {documentPreviewUrl ? (
                <iframe 
                  src={documentPreviewUrl} 
                  className="w-full h-[500px]"
                  title="Document Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-[500px] text-gray-400">
                  <FileText className="h-12 w-12" />
                </div>
              )}
            </div>
            
            {/* Right side - Extracted Data */}
            <div className="col-span-8 flex flex-col overflow-hidden">
              {/* Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">
                    Campi estratti: {filledFields} / {totalFields}
                  </span>
                  <span className="text-sm text-gray-600">
                    {modifiedFields.size > 0 && `${modifiedFields.size} modifiche`}
                  </span>
                </div>
                <Progress value={(filledFields / totalFields) * 100} className="h-2" />
              </div>
              
              {/* Tabs for sections */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className={`grid grid-cols-${tabsPerRow} w-full`}>
                  {firstRowSections.map(section => (
                    <TabsTrigger key={section.key} value={section.key} className="text-xs">
                      {section.icon} {section.title.split(' ')[0]}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {secondRowSections.length > 0 && (
                  <TabsList className={`grid grid-cols-${secondRowSections.length} w-full mt-1`}>
                    {secondRowSections.map(section => (
                      <TabsTrigger key={section.key} value={section.key} className="text-xs">
                        {section.icon} {section.title.split(' ')[0]}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                )}
                
                <ScrollArea className="flex-1 mt-4">
                  {currentSections.map(section => (
                    <TabsContent key={section.key} value={section.key} className="m-0">
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <span>{section.icon}</span>
                            {section.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4">
                            {section.fields.map(fieldKey => (
                              <ExtractedFieldInput
                                key={fieldKey}
                                fieldKey={fieldKey}
                                label={getFieldLabel(fieldKey)}
                                value={editedData[fieldKey]}
                                originalValue={extraction?.flattened_data?.[fieldKey]}
                                onChange={handleFieldChange}
                                isModified={modifiedFields.has(fieldKey)}
                              />
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  ))}
                </ScrollArea>
              </Tabs>
            </div>
          </div>
        </div>
        
        <Separator className="my-4" />
        
        {/* Footer actions */}
        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={applyToCustomer}
                onChange={(e) => setApplyToCustomer(e.target.checked)}
                className="rounded"
              />
              Applica a Cliente
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={applyToContract}
                onChange={(e) => setApplyToContract(e.target.checked)}
                className="rounded"
              />
              Applica a Contratto
            </label>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button onClick={handleValidate} disabled={isValidating}>
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validazione...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Conferma Dati
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * DocumentExtractionUploader - Upload area with extraction capability
 */
export const DocumentExtractionUploader = ({ 
  contractId, 
  nodeId, 
  onExtractionComplete,
  onDocumentUploaded 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.includes('pdf')) {
      toast.error('Per favore carica un file PDF');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('contract_id', contractId);
      formData.append('contract_node_id', nodeId);
      formData.append('document_type', 'modulo_adesione');
      
      setUploadProgress(30);
      
      // Upload document
      const uploadResponse = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadProgress(60);
      setUploadedDocument(uploadResponse.data);
      
      if (onDocumentUploaded) {
        onDocumentUploaded(uploadResponse.data);
      }
      
      toast.success('Documento caricato con successo');
      
      // Start extraction
      setExtractionStatus('in_elaborazione');
      const extractResponse = await api.post(`/documents/${uploadResponse.data.id}/extract`);
      
      setUploadProgress(80);
      
      // Poll for extraction completion
      await pollExtractionStatus(extractResponse.data.extraction_id);
      
    } catch (error) {
      console.error('Upload/extraction error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il caricamento');
      setExtractionStatus('errore');
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
    }
  };
  
  const pollExtractionStatus = async (extractionId, maxAttempts = 30) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
      
      try {
        const response = await api.get(`/extractions/${extractionId}`);
        const extractionData = response.data;
        
        setExtraction(extractionData);
        setExtractionStatus(extractionData.status);
        
        if (extractionData.status === 'estratto') {
          toast.success('Estrazione completata! Verifica i dati estratti.');
          setShowVerificationDialog(true);
          return;
        }
        
        if (extractionData.status === 'errore') {
          toast.error('Errore durante l\'estrazione: ' + (extractionData.error || 'Errore sconosciuto'));
          return;
        }
        
        if (extractionData.status === 'validato') {
          return;
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }
    
    toast.error('Timeout durante l\'estrazione');
    setExtractionStatus('errore');
  };
  
  const handleValidate = async (validationData) => {
    const response = await api.post(`/extractions/${validationData.extraction_id}/validate`, validationData);
    setExtractionStatus('validato');
    setExtraction(prev => ({ ...prev, status: 'validato' }));
    
    if (onExtractionComplete) {
      onExtractionComplete(response.data);
    }
  };
  
  const handleRegeneratePdf = async () => {
    if (!uploadedDocument?.id) return;
    
    try {
      const response = await api.post(`/documents/${uploadedDocument.id}/regenerate-pdf`);
      toast.success('PDF compilato generato con successo');
      
      // Open download URL
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (error) {
      console.error('Regenerate error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la generazione del PDF');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <Card className="border-dashed border-2 hover:border-primary/50 transition-colors">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="rounded-full bg-primary/10 p-4">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            
            <div className="text-center">
              <h3 className="font-semibold">Modulo di Adesione</h3>
              <p className="text-sm text-gray-500 mt-1">
                Carica il modulo per estrarre automaticamente i dati
              </p>
            </div>
            
            {isUploading ? (
              <div className="w-full max-w-xs space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-center text-gray-500">
                  {extractionStatus === 'in_elaborazione' 
                    ? 'Estrazione dati in corso...' 
                    : 'Caricamento...'}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="modulo-upload"
                />
                <label htmlFor="modulo-upload">
                  <Button asChild variant="outline">
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      Seleziona PDF
                    </span>
                  </Button>
                </label>
              </div>
            )}
            
            {/* Status indicator */}
            {extractionStatus && (
              <div className="flex items-center gap-2">
                <ExtractionStatusBadge status={extractionStatus} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Actions after validation */}
      {extractionStatus === 'validato' && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Dati Validati</AlertTitle>
          <AlertDescription className="text-green-700">
            <div className="flex items-center justify-between mt-2">
              <span>I dati estratti sono stati applicati al contratto e al cliente.</span>
              <Button size="sm" onClick={handleRegeneratePdf}>
                <FileCheck className="h-4 w-4 mr-2" />
                Genera PDF Compilato
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Verification Dialog */}
      <ExtractionVerificationDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        extraction={extraction}
        onValidate={handleValidate}
        documentPreviewUrl={uploadedDocument?.file_path}
      />
    </div>
  );
};

/**
 * ExtractionSummaryCard - Shows summary of extraction for a document
 */
export const ExtractionSummaryCard = ({ document, onVerify, onRegenerate }) => {
  const [extraction, setExtraction] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (document?.extraction_id) {
      loadExtraction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.extraction_id]);
  
  const loadExtraction = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/documents/${document.id}/extraction`);
      setExtraction(response.data);
    } catch (error) {
      console.error('Error loading extraction:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!document?.extraction_status) return null;
  
  return (
    <Card className="bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Estrazione Dati
          </CardTitle>
          <ExtractionStatusBadge status={document.extraction_status} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : extraction ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Pagine elaborate:</span>
                <span className="font-medium">{extraction.pages_processed || '-'}</span>
              </div>
              {extraction.validated_at && (
                <div className="flex justify-between">
                  <span>Validato:</span>
                  <span className="font-medium">
                    {new Date(extraction.validated_at).toLocaleString('it-IT')}
                  </span>
                </div>
              )}
              {extraction.validated_by_name && (
                <div className="flex justify-between">
                  <span>Da:</span>
                  <span className="font-medium">{extraction.validated_by_name}</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              {document.extraction_status === 'estratto' && (
                <Button size="sm" variant="outline" onClick={onVerify} className="flex-1">
                  <Eye className="h-4 w-4 mr-1" />
                  Verifica Dati
                </Button>
              )}
              {document.extraction_status === 'validato' && (
                <Button size="sm" onClick={onRegenerate} className="flex-1">
                  <FileCheck className="h-4 w-4 mr-1" />
                  Genera PDF
                </Button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Nessun dato disponibile</p>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentExtractionUploader;
